import { Button as MuiButton, CircularProgress } from "@mui/material";
import { useHorizontalOverflow } from "components/hooks/useHorizontalOverflow";
import { Icon } from "components/Icon/Icon";
import Tooltip from "components/Tooltip/Tooltip";
import { getIsReadonly, getRecordConfig } from "config/resourceUtils";
import { getUserData, getUserPermissionData } from "containers/Auth/Auth.selectors";
import { hasCreateUserPermission } from "containers/Auth/permissions";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import { getUncommittedGitLogData } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { bootstrapUserNameSelector } from "containers/Workspaces/Workspaces.selectors";
import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RouterContext } from "utils/RouterManager/RouterManager";
import { ActionData, ActionSignature } from "./Action";
import "./InlineActionButtons.scss";
import InlineActionsOverflow, { OverflowActionItem } from "./InlineActionsOverflow";
interface InlineActionButtonsProps {
    children?: React.ReactNode;
    resourceData: any;
    afterButtonAction: () => void;
    hideGoToParent?: boolean;
}

export default function InlineActionButtons({
    children,
    resourceData,
    afterButtonAction,
    hideGoToParent,
}: InlineActionButtonsProps) {
    const dispatch = useDispatch();
    const history = useHistory();
    const activeProjectId = useSelector(getActiveOrDefaultProjectId);
    const userData = useSelector(getUserData);
    const permissionData = useSelector(getUserPermissionData);
    const canUserCreateUser = hasCreateUserPermission(permissionData);
    const resourceConfig = getRecordConfig(resourceData?.resource_type?.toLowerCase());
    const allUncommittedData = useContext(RouterContext).allUncommittedData;
    const uncommittedDataByPath = getUncommittedGitLogData({ allUncommittedData, resourceData });
    const bootstrapUserName = useSelector(bootstrapUserNameSelector);
    const [loadingActionKey, setLoadingActionKey] = useState<string | null>(null);

    const actions: any = resourceConfig?.actions;

    // Build the list of actions actually rendered for this record ONCE, so the
    // inline icon row and the overflow menu stay in lockstep (same omit rules).
    const location = window.location;
    const isLiveMonitorPage = location.pathname.split("/")[1] === "workspace";
    const visibleActions: Array<{ action: ActionSignature; actionData: ActionData }> = [];
    if (actions && resourceData) {
        for (const action of (actions.shown ?? []) as ActionSignature[]) {
            const actionData: ActionData = {
                resourceData,
                activeProjectId,
                userData,
                resourceType: resourceData.resource_type?.toLowerCase(),
                isReadOnly: getIsReadonly(resourceConfig, resourceData),
                uncommittedData: uncommittedDataByPath,
                canUserCreateUser,
                bootstrapUserName,
            };
            if (action.omitWhen && action.omitWhen(actionData)) continue;
            // goToParent is hidden off the live-monitor page when hideGoToParent is set.
            if (action.key === "goToParent" && !isLiveMonitorPage && hideGoToParent) continue;
            visibleActions.push({ action, actionData });
        }
    }

    const { ref: overflowRef, isOverflowing } = useHorizontalOverflow<HTMLDivElement>([
        visibleActions.length,
        !!children,
    ]);
    if (!actions) return null;

    const runAction = (action: ActionSignature, actionData: ActionData) => {
        setLoadingActionKey(action.key);
        Promise.resolve(action.action(actionData, dispatch, history))
            .then(afterButtonAction)
            .finally(() => setLoadingActionKey(null));
    };

    // The overflow menu lists every rendered action (not just the clipped ones),
    // so a user always has access to the full set regardless of column width.
    const overflowItems: OverflowActionItem[] = visibleActions.map(({ action, actionData }) => ({
        key: action.key,
        label: action.label(actionData),
        icon: action.inlineActionIcon
            ? action.inlineActionIcon(actionData)
            : action.icon(actionData),
        disabled: loadingActionKey !== null || action.disableWhen?.(actionData),
        onClick: () => runAction(action, actionData),
    }));

    return (
        <div className="inlineActionButtonsCell">
            <div
                ref={overflowRef}
                className={`inlineActionButtons${isOverflowing ? " isClipped" : ""}`}
            >
                {visibleActions.map(({ action, actionData }) => (
                    <Tooltip overlay={action.label(actionData)} key={`action${action?.key}`}>
                        <MuiButton
                            key={`action${action?.key}`}
                            aria-label={action.label(actionData)}
                            size="small"
                            variant="outlined"
                            onClick={() => runAction(action, actionData)}
                            disabled={
                                loadingActionKey !== null || action.disableWhen?.(actionData)
                            }
                        >
                            {loadingActionKey === action.key ? (
                                <CircularProgress size={19} />
                            ) : (
                                <Icon
                                    icon={
                                        action.inlineActionIcon
                                            ? action.inlineActionIcon(actionData)
                                            : action.icon(actionData)
                                    }
                                />
                            )}
                        </MuiButton>
                    </Tooltip>
                ))}
                {children}
            </div>
            {isOverflowing && <InlineActionsOverflow items={overflowItems} />}
        </div>
    );
}
